import {apolloClient} from "@App/graphql/client";
import {GarageListQuery, GET_GARAGE_LIST} from "@App/graphql/queries";
import {useEffect} from 'react';
import {useSearchParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {actionCreators, State, store} from '@App/state'
import {bindActionCreators} from "@reduxjs/toolkit";
import {useDates} from "@App/hooks/useDates";
import {GET_SLOT, SlotQuery} from "@App/graphql/queries/getSlot";

/**
 * A custom hook that checks if all needed data was loaded from the api on component mount
 * and returns a method to parse the data to the url query parameters.
 */
export const useGarageParameters = () => {
    const dispatch = useDispatch();
    const {chooseGarage, chooseSlot} = bindActionCreators(actionCreators, dispatch)
    const [searchParams] = useSearchParams()

    const garage = useSelector((state: State) => state.orders.garage)
    const garageQuery = parseInt(searchParams.get('garage'))
    const slotQuery = parseInt(searchParams.get('slot'))
    const slotSessionQuery = searchParams.get('session')

    const {formatDate} = useDates()
    const organization = useSelector((state: State) => state.orders.organization)
    const service = useSelector((state: State) => state.orders.service)
    const brand = useSelector((state: State) => state.filters.brand)

    const generation = useSelector((state: State) => state.orders.generation)
    const motorization = useSelector((state: State) => state.orders.motorization)
    const body = useSelector((state: State) => state.orders.body)
    const gearbox = useSelector((state: State) => state.orders.gearbox)
    const mileage = useSelector((state: State) => state.orders.mileage)
    const initialRegistrationDate = useSelector((state: State) => state.orders.initialRegistrationDate)
    const subRepairs = useSelector((state: State) => state.orders.subRepairs)
    const equipments = useSelector((state: State) => state.orders.equipments)

    useEffect(() => {
        // get garage from api if not in state
        if (garageQuery && (!garage || garage.id !== garageQuery) && service) {
            apolloClient.query<GarageListQuery>({
                query: GET_GARAGE_LIST, variables: {
                    id: garageQuery,
                    organization: organization.id,
                    brand: brand ? brand.id : null,
                    generation: generation ? generation.id : null,
                    motorization: motorization ? motorization.id : null,
                    body: body ? body.id : null,
                    gearbox: gearbox ? gearbox.id : null,
                    mileage: mileage,
                    withCalculation: organization.settings.garage_list.display_prices ?? false,
                    registrationDate: initialRegistrationDate ? formatDate(initialRegistrationDate, 'yyyy-MM-dd') : null,
                    services: [service.id],
                    equipments: equipments.map(equipment => equipment.name),
                    subRepairs: subRepairs,
                }
            }).then((result) => {
                chooseGarage(result.data.organization.garages.data[0])
            })
        }
        if (slotQuery && slotSessionQuery) {
            apolloClient.query<SlotQuery>({
                query: GET_SLOT, variables: {
                    id: slotQuery,
                    sessionId: slotSessionQuery,
                }
            }).then((result) => {
                chooseSlot(result.data.slot)
            })
        }
    }, [service])

    const garageQueryParams = () => {
        return {
            // add service if existing
            ...(store.getState().orders.garage && {
                garage: store.getState().orders.garage.id.toString()
            }),
        }
    }

    return {garageQueryParams}
}

